import React, { useState, useEffect } from 'react';
import { Row, Col, Alert, Spinner, Badge, Card, Table, Form } from 'react-bootstrap';
import { Calendar, PackageCheck, AlertCircle, CheckCircle2, Clock, Edit, Search } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { getReservasByDataDevolucao } from '../../services/firestoreService';
import { Reserva } from '../../types';
import { formatNumeroReserva } from '../../utils/reservaUtils';
import { Button } from '../../app/components';
import MainHeader from '../Common/MainHeader/MainHeader';
import { Pagination } from '../Common/Pagination';
import DevolucaoModal from './DevolucaoModal';
import './Devolucoes.css';

const DevolucoesList: React.FC = () => {
    const { user } = useAuth();
    const userId = user?.uid;

    const [dataSelecionada, setDataSelecionada] = useState<string>(() => {
        return new Date().toLocaleDateString('en-CA');
    });
    const [devolucoes, setDevolucoes] = useState<Reserva[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedDevolucao, setSelectedDevolucao] = useState<Reserva | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('todos');

    const loadDevolucoes = async () => {
        if (!userId) return;

        setLoading(true);
        setError(null);

        try {
            const result = await getReservasByDataDevolucao(userId, dataSelecionada);

            if (result.success && result.data) {
                setDevolucoes(result.data);
            } else {
                setError(result.error || 'Erro ao carregar devoluções');
            }
        } catch (err: any) {
            setError(err.message || 'Erro ao carregar devoluções');
        } finally {
            setLoading(false);
        }
    };

    // Carregar devoluções quando a data mudar
    useEffect(() => {
        if (userId) {
            loadDevolucoes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSelecionada, userId]);

    // Resetar página ao mudar data ou filtros
    useEffect(() => {
        setCurrentPage(1);
    }, [dataSelecionada, searchTerm, statusFilter]);

    // Calcular estatísticas
    const totalDevolucoes = devolucoes.length;
    const devolucoesRealizadas = devolucoes.filter(d => d.devolucaoRealizada).length;
    const devolucoesPendentes = totalDevolucoes - devolucoesRealizadas;

    // Filtros
    const devolucoesFiltradas = devolucoes.filter((d) => {
        const matchSearch = searchTerm.trim() === '' ||
            (d.clienteNome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            formatNumeroReserva(d.numeroReserva || '').toLowerCase().includes(searchTerm.toLowerCase());

        const matchStatus =
            statusFilter === 'todos' ||
            (statusFilter === 'devolvido' && d.devolucaoRealizada === true) ||
            (statusFilter === 'pendente' && !d.devolucaoRealizada);

        return matchSearch && matchStatus;
    });

    // Paginação
    const totalPages = Math.ceil(devolucoesFiltradas.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const devolucoesPaginadas = devolucoesFiltradas.slice(startIndex, startIndex + itemsPerPage);

    // Funções auxiliares
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    const handleEditDevolucao = (reserva: Reserva) => {
        setSelectedDevolucao(reserva);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedDevolucao(null);
    };

    const getStatusBadge = (devolucaoRealizada: boolean) => {
        return devolucaoRealizada ? (
            <Badge bg="success" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                Devolvido
            </Badge>
        ) : (
            <Badge bg="warning" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                Pendente
            </Badge>
        );
    };

    return (
        <div>
            {/* Header Padronizado */}
            <MainHeader
                title="Gestão de Devoluções"
                subtitle="Gerencie as devoluções previstas para o dia selecionado"
                icon={<PackageCheck size={32} />}
            />

            {/* Filtros + Seletor de Data */}
            <Row className="mb-4">
                <Col>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                        <Card.Body className="p-4">
                            <Row className="align-items-end g-3">
                                <Col xs={12} md={3}>
                                    <label htmlFor="dataDevolucao" className="form-label mb-1 fw-semibold text-muted">
                                        Data de Devolução
                                    </label>
                                    <input
                                        type="date"
                                        id="dataDevolucao"
                                        className="form-control"
                                        value={dataSelecionada}
                                        onChange={(e) => setDataSelecionada(e.target.value)}
                                        style={{ border: '2px solid #e9ecef', borderRadius: '12px' }}
                                    />
                                </Col>
                                <Col xs={12} md={5}>
                                    <label className="form-label mb-1 fw-semibold text-muted">Buscar</label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Buscar por cliente ou nº da reserva..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} md={2}>
                                    <label className="form-label mb-1 fw-semibold text-muted">Status</label>
                                    <Form.Select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        style={{ border: '2px solid #e9ecef', borderRadius: '12px' }}
                                    >
                                        <option value="todos">Todos</option>
                                        <option value="pendente">Pendente</option>
                                        <option value="devolvido">Devolvido</option>
                                    </Form.Select>
                                </Col>
                                {(searchTerm || statusFilter !== 'todos') && (
                                    <Col xs={12} md={2}>
                                        <button
                                            className="btn btn-outline-secondary w-100"
                                            onClick={() => { setSearchTerm(''); setStatusFilter('todos'); }}
                                            style={{ borderRadius: '12px' }}
                                        >
                                            Limpar filtros
                                        </button>
                                    </Col>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Estatísticas */}
            <div className="stats-grid mb-4 fade-in-up" style={{ animationDelay: '0.2s' }}>
                <div className="stat-card stat-card-primary">
                    <div className="stat-card-icon">
                        <PackageCheck size={28} />
                    </div>
                    <div className="stat-card-value">{totalDevolucoes}</div>
                    <div className="stat-card-label">Total do Dia</div>
                </div>

                <div className="stat-card stat-card-success">
                    <div className="stat-card-icon">
                        <CheckCircle2 size={28} />
                    </div>
                    <div className="stat-card-value">{devolucoesRealizadas}</div>
                    <div className="stat-card-label">Devolvidos</div>
                </div>

                <div className="stat-card stat-card-warning">
                    <div className="stat-card-icon">
                        <Clock size={28} />
                    </div>
                    <div className="stat-card-value">{devolucoesPendentes}</div>
                    <div className="stat-card-label">Pendentes</div>
                </div>
            </div>

            {/* Erro */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    <AlertCircle size={20} className="me-2" />
                    {error}
                </Alert>
            )}

            {/* Loading */}
            {loading && (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Carregando devoluções...</p>
                </div>
            )}

            {/* Lista de Devoluções */}
            {!loading && (
                <>
                    {devolucoes.length === 0 ? (
                        <div className="empty-state">
                            <PackageCheck size={64} />
                            <h5>Nenhuma devolução prevista para esta data</h5>
                            <p className="text-muted">
                                Selecione outra data para visualizar as devoluções
                            </p>
                        </div>
                    ) : devolucoesFiltradas.length === 0 ? (
                        <div className="empty-state">
                            <Search size={64} />
                            <h5>Nenhuma devolução encontrada</h5>
                            <p className="text-muted">Tente ajustar os filtros de busca</p>
                        </div>
                    ) : (
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                            <Card.Body className="p-0">
                                <Table responsive className="mb-0 modern-table">
                                    <thead className="table-light text-dark">
                                        <tr>
                                            <th>Cliente</th>
                                            <th>Nº Reserva</th>
                                            <th>Retirada</th>
                                            <th>Produtos</th>
                                            <th>Status</th>
                                            <th className="text-center" style={{ width: '120px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {devolucoesPaginadas.map((devolucao) => (
                                            <tr key={devolucao.id} className="modern-table-row">
                                                <td className="fw-bold text-dark">{devolucao.clienteNome}</td>
                                                <td>
                                                    <span className="fw-semibold text-primary" style={{ fontFamily: 'monospace' }}>
                                                        {formatNumeroReserva(devolucao.numeroReserva || '')}
                                                    </span>
                                                </td>
                                                <td>{formatDate(devolucao.dataRetirada)}</td>
                                                <td>
                                                    <span >
                                                        {devolucao.produtos.length} {devolucao.produtos.length === 1 ? 'produto' : 'produtos'}
                                                    </span>
                                                </td>
                                                <td>{getStatusBadge(devolucao.devolucaoRealizada || false)}</td>
                                                <td className="text-center justify-content-center d-flex">
                                                    <Button
                                                        btnColor="primary"
                                                        onClick={() => handleEditDevolucao(devolucao)}
                                                        extraCss="d-flex align-items-center justify-content-center"
                                                        style={{ padding: '8px', borderRadius: '8px' }}
                                                    >
                                                        <Edit size={16} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}

                    {devolucoesFiltradas.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={devolucoesFiltradas.length}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                            onItemsPerPageChange={(value) => { setItemsPerPage(value); setCurrentPage(1); }}
                            className="mt-4"
                        />
                    )}
                </>
            )}

            {/* Modal de Edição */}
            <DevolucaoModal
                show={showModal}
                onHide={handleCloseModal}
                reserva={selectedDevolucao}
                onUpdate={loadDevolucoes}
            />
        </div>
    );
};

export default DevolucoesList;
